import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Switch, FormControlLabel, Container, Typography, Box, Paper, List, ListItem, ListItemText } from '@mui/material';

const generateKey = async () => {
  const rawKey = crypto.getRandomValues(new Uint8Array(32)); // 256-bit key
  return btoa(String.fromCharCode(...rawKey)); // Encode en base64 pour stockage
};

const deriveKey = async (base64Key) => {
  const keyBuffer = new TextEncoder().encode(base64Key);
  const hashBuffer = await crypto.subtle.digest("SHA-256", keyBuffer);
  return await crypto.subtle.importKey(
    "raw",
    hashBuffer.slice(0, 32), // Assure une longueur de 256 bits
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );
};

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const encrypt = async (content, base64Key) => {
  const encryptKey = await deriveKey(base64Key);
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    encryptKey,
    encoder.encode(content)
  );

  return btoa(JSON.stringify({
    iv: arrayBufferToBase64(iv),
    data: arrayBufferToBase64(encryptedData)
  }));
};


const decrypt = async (encryptedString, base64Key) => {
  const encryptedContent = JSON.parse(atob(encryptedString));
  const encryptKey = await deriveKey(base64Key);
  const decoder = new TextDecoder();
  const iv = new Uint8Array(atob(encryptedContent.iv).split("").map(c => c.charCodeAt(0)));
  const encryptedData = new Uint8Array(atob(encryptedContent.data).split("").map(c => c.charCodeAt(0)));
  
  const decryptedData = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    encryptKey,
    encryptedData
  );
  
  return decoder.decode(decryptedData);
};

function App() {
  const [showNoteMaster, setShowNoteMaster] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [password, setPassword] = useState('');
  const [decryptKey, setDecryptKey] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isPlainText, setIsPlainText] = useState(false);
  const [anchors, setAnchors] = useState([]);
  const textareaRef = useRef(null);
  const [noteMasterAccessKey, setNoteMasterAccessKey] = useState('');
  const [noteMasterPassword, setNoteMasterPassword] = useState('');
  const [noteMasterActive, setNoteMasterActive] = useState(false);
  const [noteMasterTitle, setNoteMasterTitle] = useState('');
  const [notes, setNotes] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;

const handleCreateNoteMaster = async () => {
  try {
    const response = await fetch(`${API_URL}/api/notemaster/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: noteMasterTitle,
        access_key: noteMasterAccessKey,
        password: noteMasterPassword,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('NoteMaster créé:', data);
    } else {
      console.error("Erreur lors de la création de NoteMaster");
    }
  } catch (error) {
    console.error("Erreur de création de NoteMaster:", error);
  }
};


const handleFetchAccessKeys = async () => {
  try {
    const response = await fetch(`${API_URL}/api/notemaster/notes/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        note_master_access_key: noteMasterAccessKey,
        note_master_password: noteMasterPassword,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.access_keys) {
        setNotes(data.access_keys); // Supposons que `access_keys` est un tableau à afficher
      } else {
        console.error("Clés d'accès non trouvées dans la réponse");
      }
    } else {
      console.error("Erreur lors de la récupération des clés d'accès");
    }
  } catch (error) {
    console.error("Erreur lors de l'appel de l'API:", error);
  }
};

  const handleListItemClick = (accessKey, password) => {
    setAccessKey(accessKey);
    setPassword(password);
  };

  const handleCreate = async () => {
    try {
      const encryptedTitle = await encrypt(title, decryptKey);
      const encryptedBody = await encrypt(body, decryptKey);

      const response = await fetch(`${API_URL}/api/notes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          action: 'create',
          access_key: accessKey, 
          password, 
          title: encryptedTitle,
          'note_master_access_key': noteMasterAccessKey,
          'note_master_password': noteMasterPassword,
          content: encryptedBody  
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTitle(await decrypt(data.title, decryptKey));
        setBody(await decrypt(data.content, decryptKey));
      } else {
        console.error('Erreur lors de la création de la note');
      }
    } catch (error) {
      console.error('Erreur de soumission:', error);
    }
  };

  const handleGet = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'retrieve',
          access_key: accessKey,
          password
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const decryptedTitle = await decrypt(data.title, decryptKey);
        const decryptedBody = await decrypt(data.content, decryptKey);
        setTitle(decryptedTitle);
        setBody(decryptedBody);
      } else {
        console.error('Erreur lors de la récupération de la note');
      }
    } catch (error) {
      console.error('Erreur de récupération:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      const encryptedTitle = await encrypt(title, decryptKey);
      const encryptedBody = await encrypt(body, decryptKey);

      const response = await fetch(`${API_URL}/api/notes/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          action: 'update',
          access_key: accessKey,
          password,
          title: encryptedTitle, 
          content: encryptedBody 
        }),
      });

      if (!response.ok) {
        console.error('Erreur lors de la mise à jour de la note');
      }
    } catch (error) {
      console.error('Erreur de mise à jour:', error);
    }
  };


    useEffect(() => {
    const lines = body.split('\n');
    const newAnchors = lines
      .map((line, index) => line.startsWith('>>>>>') ? { text: line, index } : null)
      .filter(line => line !== null);
    
    setAnchors(newAnchors);
  }, [body]);



  const scrollToLine = (lineNumber) => {
    if (textareaRef.current) {
      const lines = body.split('\n');
      const position = lines.slice(0, lineNumber).reduce((acc, line) => acc + line.length + 1, 0);
      textareaRef.current.setSelectionRange(position, position);
      textareaRef.current.focus();
    }
  };

  return (
    <Container maxWidth="sm" sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Application de Notes
      </Typography>

      <Button variant="contained" onClick={() => setShowNoteMaster(!showNoteMaster)}>
        {showNoteMaster ? 'Masquer' : 'Afficher'} Notes Master
      </Button>

      {showNoteMaster && (
        <Paper sx={{ padding: 2, marginBottom: 2 }} id="note_master_section">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="NoteMaster Access Key"
              variant="outlined"
              value={noteMasterAccessKey}
              onChange={(e) => setNoteMasterAccessKey(e.target.value)}
              fullWidth
            />
            <TextField
              label="NoteMaster Password"
              variant="outlined"
              type="password"
              value={noteMasterPassword}
              onChange={(e) => setNoteMasterPassword(e.target.value)}
              fullWidth
            />
            <Button variant="contained" color="primary" onClick={handleCreateNoteMaster}>
              Create NoteMaster
            </Button>
            <Button variant="contained" color="info" onClick={handleFetchAccessKeys}>
              Fetch Access Keys
            </Button>

            <Typography variant="h6">Clés d'accès</Typography>
            <List>
              {notes.map((note, idx) => (
                <ListItem 
                  key={idx} 
                  button 
                  onClick={() => handleListItemClick(note.access_key, note.pw)}
                  data-password={note.pw} // Stocker le mot de passe en tant qu'attribut de données
                >
                  <ListItemText primary={note.access_key} />
                </ListItem>
              ))}
            </List>

          </Box>
        </Paper>
      
      )}


      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Access Key"
            variant="outlined"
            value={accessKey}
            onChange={(e) => setAccessKey(e.target.value)}
            fullWidth
          />
          <TextField
            label="Password"
            variant="outlined"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
          />
          <Button variant="contained" color="primary" onClick={handleCreate}>
            Create
          </Button>
          <Button variant="contained" color="secondary" onClick={handleGet}>
            Get
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Decryption Key"
            variant="outlined"
            value={decryptKey}
            type="password"
            onChange={(e) => setDecryptKey(e.target.value)}
            fullWidth
          />
          <TextField
            label="Title"
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={isPlainText}
                onChange={() => setIsPlainText(!isPlainText)}
              />
            }
            label="Afficher en texte brut"
          />
          <List>
            {anchors.map((anchor, idx) => (
              <ListItem button key={idx} onClick={() => scrollToLine(anchor.index)}>
                <ListItemText primary={anchor.text} />
              </ListItem>
            ))}
          </List>
          {isPlainText ? (
            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', height: 'calc(100vh - 400px)', overflow: 'auto' }}>
              {body.split('\n').map((line, index) => (
                <div key={index}>
                  {line}
                </div>
              ))}
            </pre>
          ) : (
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              style={{ width: '100%', height: 'calc(100vh - 400px)', overflow: 'auto', fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit', padding: '8px', boxSizing: 'border-box' }}
            />
          )}
          <Button variant="contained" color="success" onClick={handleUpdate}>
            Update
          </Button>
        </Box>
      </Paper>




    </Container>
  );
};

export default App;